import { motion } from "framer-motion";
import { useScrollAnimation } from "../hooks/useScrollAnimation";
import { ArrowUpRight } from "lucide-react";

const steps = [
  { num: "01", title: "Discover", desc: "We sit with stakeholders, define the wedge, and map success metrics before any build starts." },
  { num: "02", title: "Architect", desc: "Solution architects design the system and data flow, signed off before development begins." },
  { num: "03", title: "Build", desc: "A dedicated pod ships in two-week sprints, with demos so you see progress the whole way." },
  { num: "04", title: "Test", desc: "Automated suites, model evaluation, and manual QA passes catch issues before they ship." },
  { num: "05", title: "Launch", desc: "Controlled go-live with CI/CD, cloud provisioning, and a rollback plan ready if needed." },
  { num: "06", title: "Support", desc: "SLA-backed monitoring, retraining, and reporting keep the system performing post-launch." },
];

function FadeUp({ children, delay = 0, className = "" }) {
  const [ref, visible] = useScrollAnimation(0.1);
  return (
    <motion.div ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={visible ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, delay, ease: "easeOut" }}
      className={className}>
      {children}
    </motion.div>
  );
}

export default function Process() {
  return (
    <section id="process" className="relative py-24 bg-paper overflow-hidden">
      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <FadeUp className="mb-10">
          <span className="eyebrow">03 — Process</span>
        </FadeUp>

        <div className="grid lg:grid-cols-12 gap-10 mb-12">
          <FadeUp delay={0.1} className="lg:col-span-4">
            <h2 className="text-4xl sm:text-5xl font-bold leading-[0.96] tracking-[-0.03em] text-ink">
              How we
              <br />
              deliver.
            </h2>
          </FadeUp>

          <div className="lg:col-span-8 flex flex-col border-t-2 border-ink">
            {steps.map(({ num, title, desc }, i) => (
              <FadeUp key={num} delay={0.12 + i * 0.05}>
                <div className="group grid sm:grid-cols-12 gap-2 sm:gap-6 py-5 items-start transition-colors duration-200 hover:bg-ink/[0.02]"
                  style={{ borderBottom: "1px solid rgba(14,14,13,0.12)" }}>
                  <div className="sm:col-span-1">
                    <span className="text-xs font-mono text-ink-300">{num}</span>
                  </div>
                  <div className="sm:col-span-3">
                    <h3 className="text-ink font-semibold text-base">{title}</h3>
                  </div>
                  <div className="sm:col-span-7">
                    <p className="text-ink-500 text-sm leading-relaxed">{desc}</p>
                  </div>
                  <div className="sm:col-span-1 flex sm:justify-end">
                    <ArrowUpRight size={15}
                      className="text-ink-300 group-hover:text-ink transition-all duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>

        {/* Timeline highlight block */}
        <FadeUp delay={0.3}>
          <div className="btn-lime rounded-2xl px-8 py-10 flex flex-col sm:flex-row items-start sm:items-end justify-between gap-6">
            <div className="flex items-baseline gap-3">
              <span className="text-5xl sm:text-6xl font-bold tracking-tight">0 → 1</span>
              <span className="text-sm font-medium text-ink/60">in 90 days</span>
            </div>
            <div className="flex gap-8">
              {["Discover", "Build", "Launch"].map((s, i) => (
                <div key={s}>
                  <div className="text-xs font-mono text-ink/50 mb-1">0{i + 1}</div>
                  <div className="text-sm font-semibold">{s}</div>
                </div>
              ))}
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}
